import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageCircle, User } from 'lucide-react';
import { profileData } from '../data/mock';

const PROFILE_IMAGE = "https://customer-assets.emergentagent.com/job_motion-profile-3/artifacts/mxrz3tmc_Screenshot%202026-02-01%20at%202.22.10%E2%80%AFPM.png";

const aboutMessages = [
  { speaker: 'visitor', text: 'Tell me about yourself?' },
  { speaker: 'sai', text: "Great question! I'm a software engineer who's passionate about building intelligent systems." },
  { speaker: 'sai', text: 'I specialize in AI/ML, particularly RAG systems and LLM-powered applications.' },
  { speaker: 'sai', text: 'What sets me apart? I obsess over user experience and rigorously test everything before deployment.' }
];

const ChatMessage = ({ message, index, isInView }) => {
  const isVisitor = message.speaker === 'visitor';
  
  return (
    <motion.div
      initial={{ opacity: 0, x: isVisitor ? -30 : 30, y: 20 }}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.3 }}
      className={`flex gap-3 ${isVisitor ? 'justify-start' : 'justify-end'}`}
    >
      {isVisitor && (
        <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
          <User size={18} className="text-white/60" />
        </div>
      )}
      
      <div
        className={`max-w-md px-5 py-3 rounded-2xl backdrop-blur-sm ${
          isVisitor
            ? 'bg-white/5 border border-white/10 rounded-tl-md'
            : 'bg-gradient-to-r from-[#ffb79c]/15 to-[#f5d89a]/10 border border-[#ffb79c]/20 rounded-tr-md'
        }`}
      >
        <p className={`text-base ${isVisitor ? 'text-white/70' : 'text-white/90'}`}>
          {message.text}
        </p>
      </div>
      
      {!isVisitor && (
        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ffb79c] to-[#f5d89a] flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-[#0d0d0f]">SP</span>
        </div>
      )}
    </motion.div>
  );
};

const StatCard = ({ value, label, index, isInView }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ delay: 1 + index * 0.1 }}
      whileHover={{ scale: 1.02, y: -4 }}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 text-center hover:border-[#ffb79c]/30 hover:bg-[#ffb79c]/5 transition-all cursor-default backdrop-blur-sm"
    >
      <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#ffb79c] to-[#f5d89a]">
        {value}
      </div>
      <div className="text-white/50 text-sm mt-2">{label}</div>
    </motion.div>
  );
};

const AboutSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const highlights = [
    { label: 'Years Experience', value: '3+' },
    { label: 'Projects Built', value: '15+' },
    { label: 'Technologies', value: '20+' },
    { label: 'AI Solutions', value: '10+' }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-32 bg-[#0a0a0c] relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#ffb79c]/5 via-[#f5d89a]/3 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#e8c4c4]/5 rounded-full blur-3xl" />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-12"
        >
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ffb79c]/20 to-[#ffd4c4]/10 border border-[#ffb79c]/20 flex items-center justify-center">
            <MessageCircle className="text-[#ffb79c]" size={24} />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">Let's Chat</h2>
            <p className="text-white/50">Get to know me better</p>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <div className="space-y-6">
            {aboutMessages.map((message, index) => (
              <ChatMessage
                key={index}
                message={message}
                index={index}
                isInView={isInView}
              />
            ))}
          </div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              {highlights.map((item, index) => (
                <StatCard
                  key={item.label}
                  value={item.value}
                  label={item.label}
                  index={index}
                  isInView={isInView}
                />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="bg-gradient-to-br from-[#ffb79c]/10 via-[#f5d89a]/5 to-[#a5d8ff]/5 border border-[#ffb79c]/15 rounded-2xl p-8 backdrop-blur-sm"
            >
              <h3 className="text-xl font-semibold text-white mb-4">My Philosophy</h3>
              <p className="text-white/60 leading-relaxed">
                "As technology evolves, I maintain strong fundamentals while staying flexible 
                and adapting to new project requirements. I believe in building systems that 
                are not just functional, but delightful to use."
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#ffb79c] to-[#f5d89a] flex items-center justify-center">
                  <span className="text-lg font-bold text-[#0d0d0f]">SP</span>
                </div>
                <div>
                  <div className="text-white font-medium">{profileData.name}</div>
                  <div className="text-white/50 text-sm">{profileData.title}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
